import React, { useEffect, useState } from "react";
import { Col, Row, Stack, Table } from "react-bootstrap";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { CSVLink } from "react-csv";
import ReactPaginate from "react-paginate";
import { fetchPrograms, fetchReportPrograms } from "../../../services/httpClient";
import SelectObject from "../../ui/SelectObject";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";
import { DEFAULT_ITEMS_PER_PAGE } from "../../../constants/table";
import { getCurrentDateTime } from "../../../helpers/date";

interface ProgramTableProps {
  data?: any[];
  programList?: any[];
  showHeader?: boolean;
}

const itemsPerPage = DEFAULT_ITEMS_PER_PAGE;

export const ProgramTable = ({ data, programList, showHeader = false }: ProgramTableProps) => {
  const [programData, setProgramData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<number>();
  const [levelsUsed, setLevelsUsed] = useState<boolean>(false);
  const [paginate, setPaginate] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(0);

  const pageCount = Math.ceil(filteredData.length / itemsPerPage);
  const offset = currentPage * itemsPerPage;
  const currentData = filteredData.slice(offset, offset + itemsPerPage);

  const programHeaders = [
    { label: "Program", key: "program" },
    ...(levelsUsed ? [{ label: "Level", key: "level" }] : []),
    { label: "Case Name", key: "name" },
    { label: "First Name", key: "firstname" },
    { label: "Last Name", key: "lastname" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (data && programList) {
        setProgramData(data);
        setFilteredData(data);
        setPrograms(programList);

        const hasLevels = data.some((d: any) => d.level);
        setLevelsUsed(hasLevels);
      } else {
        await getProgramData();
      }
    };

    fetchData();
  }, [data, programList]);

  const getProgramData = async () => {
    fetchReportPrograms()
      .then((res: any) => {
        const progData = res[0]?.data ?? [];
        setProgramData(progData);
        setFilteredData(progData);

        fetchPrograms()
          .then((res: any) => {
            const programs = res[0]?.data ?? [];

            const flattenedProgs = programs.flatMap((prog: any) => {
              const levels = prog.programLevels ?? [];

              // Add level name to each programData entry
              progData.forEach((d: any) => {
                if (d.program_id === prog.id) {
                  const level = levels.find((l: any) => l.id === d.level_id);
                  d.level = level?.name;
                  if (level) {
                    setLevelsUsed(true);
                  }
                }
              });

              const levelEntries = levels.map((level: any) => {
                const levelCount = progData.filter((d: any) => d.level_id === level.id).length;

                return {
                  ...prog,
                  id: `${prog.id}-${level.id}`,
                  level_id: level.id,
                  program_id: prog.id,
                  name: `${prog.name} - ${level.name} (${levelCount})`,
                  key: `${prog.id}-${level.id}`,
                };
              });

              // Always include the base program entry
              const baseCount = progData.filter((d: any) => d.program === prog.name).length;
              const baseProgramEntry = {
                ...prog,
                id: prog.id,
                program_id: prog.id,
                name: `${prog.name} (${baseCount})`,
                key: prog.id,
              };

              return [baseProgramEntry, ...levelEntries];
            });

            setPrograms(flattenedProgs);
          })
          .catch((err: any) => console.log(err));
      })
      .catch((err: any) => console.log(err));
  };

  const updateProgram = (e: any) => {
    setSelectedProgram(e.target.value);
    const filtered = programData.filter((d: any) => {
      if (e.target.value === "0") {
        return d;
      } else {
        if (e.target.value.includes("-")) {
          // If the value includes a dash, it's a level
          const [programId, levelId] = e.target.value.split("-");
          return d.program_id == programId && d.level_id == levelId;
        }
        return d.program_id == e.target.value;
      }
    });

    setFilteredData(filtered);
    setCurrentPage(0);

    e.target.value === "0" ? setPaginate(true) : setPaginate(false);
  };

  const handlePageClick = (event: { selected: number }) => {
    setCurrentPage(event.selected);
  };

  return (
    <>
      {showHeader && (
        <Row>
          <Col>
            <h5 className="app-header-text">Current Programming</h5>
          </Col>
        </Row>
      )}
      {programData.length > 0 ? (
        <>
          <Row id="programs-filters">
            <Col xs={4} className="mb-1">
              <SelectObject
                label="Program / Level (# People)"
                initialOption="View All"
                value={selectedProgram}
                options={programs}
                onUpdate={(e: any) => updateProgram(e)}
              />
            </Col>
            <Col xs={4} className="d-flex flex-column text-end justify-content-end align-items-end">
              <Stack direction="horizontal" gap={2} className="mb-3">
                <CSVLink
                  data={programData}
                  headers={programHeaders}
                  filename={`current_programming${getCurrentDateTime()}.csv`}
                  target="_blank"
                >
                  <IconEmbeddedButton tooltipText="Data Export" icon={faDownload} />
                </CSVLink>
              </Stack>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={12}>
              <Table striped>
                <thead>
                  <tr>
                    <th>Program</th>
                    {levelsUsed && <th>Level</th>}
                    <th>Case Name</th>
                    <th>Person</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate ? (
                    currentData?.map((d: any, index: number) => (
                      <tr key={index}>
                        <td>{d.program}</td>
                        {levelsUsed && <td>{d.level ? d.level : ""}</td>}
                        <td className="first-letter">{d.name}</td>
                        <td>
                          {d.firstname} {d.lastname}
                        </td>
                      </tr>
                    ))
                  ) : filteredData.length === 0 ? (
                    <p className="mt-2">No Program Records</p>
                  ) : (
                    filteredData?.map((d: any, index: number) => (
                      <tr key={index}>
                        <td>{d.program}</td>
                        {levelsUsed && <td>{d.level ? d.level : ""}</td>}
                        <td className="first-letter">{d.name}</td>
                        <td>
                          {d.firstname} {d.lastname}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              {paginate && pageCount > 1 && (
                <div id="programs-pagination">
                  <ReactPaginate
                    previousLabel={"← Previous"}
                    nextLabel={"Next →"}
                    pageCount={pageCount}
                    onPageChange={handlePageClick}
                    containerClassName={"pagination justify-content-center"}
                    pageClassName={"page-item"}
                    pageLinkClassName={"page-link"}
                    previousClassName={"page-item"}
                    previousLinkClassName={"page-link"}
                    nextClassName={"page-item"}
                    nextLinkClassName={"page-link"}
                    activeClassName={"active"}
                    forcePage={currentPage}
                  />
                </div>
              )}
            </Col>
          </Row>
        </>
      ) : (
        <p className="mt-2">No Program Records</p>
      )}
    </>
  );
};
